import { type VoxelData, type VoxelMap } from '@/voxelTypes'
import { parseKeyX, parseKeyY, parseKeyZ } from '@/voxelMath'
import type { SnapshotMeta } from '@/document/model'
import { isChunkedVoxelGrid, type ChunkedVoxelGrid } from '@/storage/grid/ChunkedVoxelGrid'
import {
  decodePayloadToGrid,
  encodeGridPayload,
  type ChunkRLE,
  type GridPayload,
  type PaletteItem,
} from '@/storage/grid/gridCodec'
// Note: no external compression library. We prefer built-in CompressionStream/DecompressionStream.

export type CheckpointBlob = {
  fmt: 'deflate+json+chunked-rle32' | 'json+chunked-rle32'
  chunkSize: number
  data: string // base64 of compressed bytes (deflate) or raw JSON string (if fmt starts with json)
}

// Payload item/chunk shapes live in storage/grid/gridCodec.ts (shared with the grid fast path).
export type { ChunkRLE, PaletteItem } from '@/storage/grid/gridCodec'
export type CheckpointPayload = GridPayload & { meta: SnapshotMeta }
type CompressionStreamCtor = new (format: 'deflate') => TransformStream<Uint8Array, Uint8Array>
type DecompressionStreamCtor = new (format: 'deflate' | 'deflate-raw') => TransformStream<Uint8Array, Uint8Array>
type CompressionRuntime = typeof globalThis & {
  CompressionStream?: CompressionStreamCtor
  DecompressionStream?: DecompressionStreamCtor
}

// Reuse encoders/decoders to avoid tiny allocations in tight loops
const TE = new TextEncoder()
const TD = new TextDecoder()

function forEachVoxel(base: VoxelMap, fn: (k: number, v: VoxelData) => void) {
  base.forEach((v, k) => fn(k, v))
}

function materialKey(v: VoxelData): string {
  const f = v.face || ''
  const tf = v.topFace || ''
  const c = (typeof v.color === 'number') ? v.color : -1
  const m = v.material || ''
  const d = v.density ?? ''
  return `${v.type}|${c}|${f}|${tf}|${m}|${d}`
}

function buildGlobalPaletteIndex(base: VoxelMap): { palette: PaletteItem[]; index: Map<string, number> } {
  const palette: PaletteItem[] = []
  const index = new Map<string, number>()
  forEachVoxel(base, (_k, v) => {
    const mk = materialKey(v)
    if (!index.has(mk)) {
      index.set(mk, palette.length)
      palette.push({ t: v.type, c: v.color, f: v.face, tf: v.topFace, m: v.material, d: v.density })
    }
  })
  return { palette, index }
}

function chunkKey(cx: number, cy: number, cz: number): string { return `${cx},${cy},${cz}` }

function buildChunks(base: VoxelMap, chunkSize: number, paletteIndex: Map<string, number>): Map<string, Map<number, number>> {
  const chunks = new Map<string, Map<number, number>>()
  const vol = chunkSize * chunkSize * chunkSize
  forEachVoxel(base, (k, v) => {
    const x = parseKeyX(k), y = parseKeyY(k), z = parseKeyZ(k)
    const cx = Math.floor(x / chunkSize)
    const cy = Math.floor(y / chunkSize)
    const cz = Math.floor(z / chunkSize)
    const lx = x - cx * chunkSize
    const ly = y - cy * chunkSize
    const lz = z - cz * chunkSize
    const lin = lx + ly * chunkSize + lz * chunkSize * chunkSize
    if (lin < 0 || lin >= vol) return
    const mk = materialKey(v)
    let pi = paletteIndex.get(mk)
    if (pi == null) { pi = paletteIndex.size; paletteIndex.set(mk, pi) }
    const val = (pi + 1) | 0
    const key = chunkKey(cx, cy, cz)
    let m = chunks.get(key)
    if (!m) { m = new Map<number, number>(); chunks.set(key, m) }
    m.set(lin, val)
  })
  return chunks
}

function encodeChunkRLE(chunkMap: Map<number, number>, chunkSize: number): [number, number][] {
  const vol = chunkSize * chunkSize * chunkSize
  let runs: [number, number][] = []
  let last = -1
  let len = 0
  for (let i = 0; i < vol; i++) {
    const v = chunkMap.get(i) || 0
    if (i === 0) { last = v; len = 1; continue }
    if (v === last) { len++; continue }
    runs.push([len, last])
    last = v
    len = 1
  }
  runs.push([len, last])
  return runs
}

// --- Compression helpers -------------------------------------------------------

function compressionRuntime(): CompressionRuntime {
  return globalThis as CompressionRuntime
}

function isCompressionAvailable(): boolean { return !!compressionRuntime().CompressionStream }

async function compressStringDeflate(text: string): Promise<Uint8Array> {
  const CS = compressionRuntime().CompressionStream
  if (!CS) throw new Error('Cannot encode deflate: CompressionStream unavailable')

  const input = TE.encode(text)
  const stream = new CS('deflate')
  const blob = new Blob([input])
  const compressed = await new Response(blob.stream().pipeThrough(stream)).arrayBuffer()

  return new Uint8Array(compressed)
}

async function decompressDeflateToString(bytes: Uint8Array): Promise<string> {
  const DS = compressionRuntime().DecompressionStream
  if (!DS) throw new Error('Cannot decode deflate: DecompressionStream unavailable')
  
  const isZlib = bytes.length > 2 && bytes[0] === 0x78
  const fmt = isZlib ? 'deflate' : 'deflate-raw'

  const stream = new DS(fmt)
  const blobBytes = new Uint8Array(bytes.byteLength)
  blobBytes.set(bytes)
  const blob = new Blob([blobBytes.buffer])
  const decompressed = await new Response(blob.stream().pipeThrough(stream)).arrayBuffer()

  return TD.decode(new Uint8Array(decompressed))
}

function bytesToBase64(bytes: Uint8Array): string {
  let str = ''
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i])
  // eslint-disable-next-line no-undef
  return btoa(str)
}

function base64ToBytes(b64: string): Uint8Array {
  // eslint-disable-next-line no-undef
  const str = atob(b64)
  const out = new Uint8Array(str.length)
  for (let i = 0; i < str.length; i++) out[i] = str.charCodeAt(i)
  return out
}

// --- Public API ----------------------------------------------------------------

/** Builds the inner `{ palette, chunkSize, chunks }` payload. Grids take the chunk-wise path; the shape is identical. */
export function encodeCheckpointPayload(base: VoxelMap, chunkSize: number = 32): GridPayload {
  if (isChunkedVoxelGrid(base)) return encodeGridPayload(base, chunkSize)
  const { palette, index } = buildGlobalPaletteIndex(base)
  const chunks = buildChunks(base, chunkSize, index)
  return {
    palette,
    chunkSize,
    chunks: Array.from(chunks.entries()).map(([key, m]) => {
      const [cx, cy, cz] = key.split(',').map((s) => parseInt(s, 10)) as [number, number, number]
      return { origin: [cx, cy, cz] as [number, number, number], runs: encodeChunkRLE(m, chunkSize) } as ChunkRLE
    }),
  }
}

/**
 * Materializes a payload as a document base (a ChunkedVoxelGrid, ADR 0003). The payload shape is
 * the historical chunked RLE, so blobs written before the grid existed decode through the same path.
 */
export function decodeCheckpointPayload(obj: GridPayload, bounds?: SnapshotMeta['bounds'] | null): ChunkedVoxelGrid {
  return decodePayloadToGrid(obj, { bounds })
}

export async function encodeCheckpoint(base: VoxelMap, meta: SnapshotMeta, chunkSize: number = 32): Promise<CheckpointBlob> {
  const { palette, chunks } = encodeCheckpointPayload(base, chunkSize)
  // Key order (meta, palette, chunkSize, chunks) is kept identical to the historical payload.
  const payload: CheckpointPayload = { meta, palette, chunkSize, chunks }
  const json = JSON.stringify(payload)
  if (isCompressionAvailable()) {
    const bytes = await compressStringDeflate(json)
    return { fmt: 'deflate+json+chunked-rle32', chunkSize, data: bytesToBase64(bytes) }
  }
  // Fallback: store raw JSON (larger, but portable)
  return { fmt: 'json+chunked-rle32', chunkSize, data: json }
}

export async function decodeCheckpoint(blob: CheckpointBlob): Promise<{ base: VoxelMap; meta: SnapshotMeta }> {
  if (!blob) throw new Error('Missing checkpoint blob')
  const { fmt, data } = blob
  let json: string
  if (fmt === 'deflate+json+chunked-rle32') {
    const bytes = base64ToBytes(data)
    json = await decompressDeflateToString(bytes)
  } else if (fmt === 'json+chunked-rle32') {
    json = data
  } else {
    throw new Error('Unsupported checkpoint format: ' + fmt)
  }
  const obj = JSON.parse(json) as CheckpointPayload
  const base = decodeCheckpointPayload(obj, obj.meta?.bounds)
  return { base, meta: obj.meta }
}
/**
 * Checkpoint Codec: deflate(json+chunked-rle32)
 *
 * Future optimizations to consider:
 * - Precompute expandBits tables (0..31) to build Morton keys faster during decode.
 * - Compose Morton keys from chunk origin + local Morton offsets to avoid per-voxel posKeyXYZ.
 * - Binary chunk format (TypedArrays) to reduce JSON parse overhead and GC churn.
 * - Adaptive chunk sizes (8/16/32) based on document size or measured entropy.
 * - Delta-encoded keys or key runs per chunk for a “chunked(keys)” variant.
 * - Move encode/decode to a Web Worker to keep main thread responsive.
 */
